import { ProductName } from '@pypestream/api-services/urql';

export type ProjectEngineType = 'metabase' | 'posthog';

export const isAnalyticsProduct = (productName?: string): boolean =>
  productName === ProductName.Analytics;

export type MetabaseDataset = 'live' | 'sandbox';

/** Display order for the dataset segmented control (matches the design). */
export const METABASE_DATASETS: MetabaseDataset[] = ['sandbox', 'live'];

export const METABASE_DATASET_LABELS: Record<MetabaseDataset, string> = {
  live: 'Live',
  sandbox: 'Sandbox',
};

export type DashboardGroup = {
  id: string;
  name: string;
  dashboardCount: number;
};

export type MetabaseFoldersByDataset = Record<
  MetabaseDataset,
  DashboardGroup[]
>;

export type DatasetScope = {
  /** collectionId (string) -> roleId (string). */
  folders: Record<string, string>;
};

export type MetabaseAccessScope = Record<MetabaseDataset, DatasetScope>;

const createEmptyDatasetScope = (): DatasetScope => ({ folders: {} });

export const createEmptyMetabaseAccessScope = (): MetabaseAccessScope => ({
  live: createEmptyDatasetScope(),
  sandbox: createEmptyDatasetScope(),
});

export type MetabaseGrantRow = {
  dataset: MetabaseDataset;
  groupId: string;
  roleId: string;
};

export const scopeToRows = (scope: MetabaseAccessScope): MetabaseGrantRow[] =>
  METABASE_DATASETS.flatMap((dataset) =>
    Object.entries(scope[dataset].folders).map(([groupId, roleId]) => ({
      dataset,
      groupId,
      roleId,
    }))
  );

export const distinctScopeRoleIds = (scope: MetabaseAccessScope): string[] => {
  const ids = new Set<string>();
  METABASE_DATASETS.forEach((dataset) => {
    Object.values(scope[dataset].folders).forEach((roleId) => ids.add(roleId));
  });
  return [...ids];
};

export type MetabaseFolderRolePayload = {
  collectionId: number;
  roleId: string;
};

export type MetabaseDatasetAccessPayload = {
  dataset: MetabaseDataset;
  collections: MetabaseFolderRolePayload[];
};

/**
 * The backend treats the whole `metabaseAccess` array as the COMPLETE set of
 * per-folder grants and deletes any grant not present. So a dataset that lost
 * all its folders is simply omitted, and an empty result (`[]`) clears every
 * folder grant ⇒ whole-project access.
 */
export const scopeToMetabaseAccessPayload = (
  scope: MetabaseAccessScope
): MetabaseDatasetAccessPayload[] =>
  METABASE_DATASETS.reduce<MetabaseDatasetAccessPayload[]>(
    (payload, dataset) => {
      const collections = Object.entries(scope[dataset].folders)
        .filter(
          ([groupId]) => groupId !== '' && Number.isInteger(Number(groupId))
        )
        .map(([groupId, roleId]) => ({
          collectionId: Number(groupId),
          roleId,
        }));
      if (collections.length) {
        payload.push({ dataset, collections });
      }
      return payload;
    },
    []
  );

export type MetabaseAccessRow = {
  dataset: string;
  collectionId: number;
  roleId: string;
};

export const scopeFromMetabaseAccess = (
  rows?: MetabaseAccessRow[]
): MetabaseAccessScope => {
  const scope = createEmptyMetabaseAccessScope();
  (rows ?? []).forEach((row) => {
    if (!METABASE_DATASETS.includes(row.dataset as MetabaseDataset)) {
      return;
    }
    scope[row.dataset as MetabaseDataset].folders[String(row.collectionId)] =
      row.roleId;
  });
  return scope;
};

export const metabaseAccessPayloadToRows = (
  payload: MetabaseDatasetAccessPayload[]
): MetabaseAccessRow[] =>
  payload.flatMap((entry) =>
    entry.collections.map((collection) => ({
      dataset: entry.dataset,
      collectionId: collection.collectionId,
      roleId: collection.roleId,
    }))
  );
